import { atom } from "jotai";
import { z } from "zod";

import { JotaiTool } from "../../index";
import {
  canvasAtom,
  edgesAtom,
  nodesAtom,
  startStreamingProgressSetter,
  zslAtom,
} from "@zapier/canvas-state";
import { outlineAtom, partialOutlineAtom } from "@zapier/streaming";

const parameters = z.object({
  prompt: z
    .string()
    .describe(
      "A improved version of the users prompt to clarify users intent. This prompt will be used to update the system by another agent ",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Creating new system...' or 'Building workflow...'",
    ),
});

const description = ` The result of this tool will replace the current system.

      ## ALWAYS USE THIS TOOL FOR:
      - ANY changes involving zaps (new or edited)
      - Larger or complex system changes
      - Creating new systems
      - When edit_system is failing

      ## When NOT to use this tool:
      - Simple edits to fields on agents, tables, interfaces, or chatbots
      - Adding new fields to existing tables, interfaces, or chatbots
      - Removing fields from tables, interfaces, or chatbots
      - Simple addition or removal of agents, tables, interfaces, or chatbots that don't affect zaps
      
      Note: This tool completely replaces the system state with a new version created from your prompt.
      `;

export const createSystemAtom = atom<JotaiTool<z.infer<typeof parameters>>>({
  name: "create_system",
  description,
  parameters,
  execute: atom(
    null,
    async (get, set, { reactFlowInstance, submitAndWait, args }) => {
      reactFlowInstance.setViewport({ x: 0, y: 0, zoom: 1 });
      set(resetCanvasSetterAtom);
      set(startStreamingProgressSetter);
      const zsl = await submitAndWait(args.prompt);
      // Reset streaming progress after successful completion
      return {
        content: [
          {
            type: "text",
            text: `System updated with zsl: ${JSON.stringify(zsl)}`,
          },
        ],
      };
    },
  ),
});
createSystemAtom.debugLabel = "createSystemAtom";

const resetCanvasSetterAtom = atom(null, (_get, set) => {
  set(nodesAtom, []);
  set(canvasAtom, (prev) => ({
    ...prev,
    nodes: [],
    edges: [],
  }));
  set(outlineAtom, "");
  set(partialOutlineAtom, "");
  set(edgesAtom, []);
  set(zslAtom, undefined);
});
